import {useTranslation} from "react-i18next";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {GlobalContext} from "../../../utils/context/GlobalContextProvider.jsx";
import NarrowSecureLayout from "../../../layout/NarrowSecureLayout.jsx";
import LoadingComponent from "../../../components/LoadingComponent.jsx";
import {useErrorTranslation} from "../../../api/utilities/translateErrorMessage.js";
import {ProjectApi, StatusCategory, StatusChange} from "../../../api/authority/generated/index.ts";
import toast from "react-hot-toast";
import Select from "react-select";
import {IntegrationSection} from "./IntegrationDetailUi.jsx";
import Button from "../../../components/Button.jsx";
import {hasOrderStatusDraftChanges, orderStatusIds} from "./orderStatusDraft.js";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTriangleExclamation} from "@fortawesome/free-solid-svg-icons";

const categoryConfiguration = [
    {key: 'completed', category: StatusCategory.Completed},
    {key: 'received', category: StatusCategory.Received},
    {key: 'cancelled', category: StatusCategory.Cancelled},
];

function StatusCategorySection({title, description, options, value, onChange, noOptionsMessage}) {
    return (
        <IntegrationSection title={title} description={description}>
            <Select
                options={options}
                value={value}
                onChange={onChange}
                isMulti
                isClearable={false}
                className="basic-multi-select"
                classNamePrefix="select"
                placeholder={null}
                noOptionsMessage={noOptionsMessage}
            />
        </IntegrationSection>
    );
}

export default function OrderStatusSettings({project}) {

    let {t} = useTranslation();
    let {et} = useErrorTranslation();

    let {getAuthorityConfiguration} = useContext(GlobalContext);

    let [statusSettings, setStatusSettings] = useState(null);
    let [statusDraft, setStatusDraft] = useState(null);
    let [updating, setUpdating] = useState(false);
    let [refresh, setRefresh] = useState(false);

    useEffect(() => {
        if (refresh) {
            setRefresh(false);
            return;
        }

        let active = true;
        setStatusSettings(null);
        setUpdating(true);

        new ProjectApi(getAuthorityConfiguration())
            .orderStatusList({
                projectId: project.id,
            })
            .then((data) => {
                if (!active) return;
                setUpdating(false);
                setStatusSettings(data);
                setStatusDraft({
                    completed: data.completed,
                    received: data.received,
                    cancelled: data.cancelled,
                });
            })
            .catch((e) => {
                if (active) et(e).then((message) => { if (message) toast.error(message); });
            });

        return () => { active = false; };

    }, [project.id, refresh]);

    let statusLists = useMemo(
        () => {
            let statusLists = {
                completedPossible: [],
                completed: [],
                receivedPossible: [],
                received: [],
                cancelledPossible: [],
                cancelled: [],
            };

            if (!statusSettings || !statusDraft) return statusLists;

            let isStatusCompleted = (status) => statusDraft.completed.filter(
                (completedStatus) => completedStatus.value === status.value
            ).length > 0;

            let isStatusReceived = (status) => statusDraft.received.filter(
                (receivedStatus) => receivedStatus.value === status.value
            ).length > 0;

            let isStatusCancelled = (status) => statusDraft.cancelled.filter(
                (cancelledStatus) => cancelledStatus.value === status.value
            ).length > 0;

            if (statusSettings) {
                for (let status of statusSettings.all) {
                    let isCompleted = isStatusCompleted(status);
                    let isReceived = isStatusReceived(status);
                    let isCancelled = isStatusCancelled(status);

                    if (isCompleted) {
                        statusLists.completed.push(status);
                        statusLists.completedPossible.push(status);
                    }

                    if (isReceived) {
                        statusLists.received.push(status);
                        statusLists.receivedPossible.push(status);
                    }

                    if (isCancelled) {
                        statusLists.cancelled.push(status);
                        statusLists.cancelledPossible.push(status);
                    }

                    if (!isCompleted && !isReceived && !isCancelled) {
                        statusLists.completedPossible.push(status);
                        statusLists.receivedPossible.push(status);
                        statusLists.cancelledPossible.push(status);
                    }
                }
            }

            return statusLists;
        }, [statusDraft, statusSettings]
    );

    const saveStatusDraft = async () => {
        if (!hasOrderStatusDraftChanges(statusSettings, statusDraft)) return;

        setUpdating(true);
        const api = new ProjectApi(getAuthorityConfiguration());

        try {
            const removals = [];
            const additions = [];

            for (const {key, category} of categoryConfiguration) {
                const persistedIds = orderStatusIds(statusSettings[key]);
                const draftedIds = orderStatusIds(statusDraft[key]);

                for (const statusId of persistedIds.filter((statusId) => !draftedIds.includes(statusId))) {
                    removals.push({category, change: StatusChange.Remove, statusId});
                }
                for (const statusId of draftedIds.filter((statusId) => !persistedIds.includes(statusId))) {
                    additions.push({category, change: StatusChange.Add, statusId});
                }
            }

            for (const projectStatusUpdate of [...removals, ...additions]) {
                await api.modifyOrderStatusList({projectId: project.id, projectStatusUpdate});
            }
            toast.success(t('saved'));
        } catch (error) {
            const message = await et(error);
            if (message) toast.error(message);
        } finally {
            setRefresh(true);
        }
    };

    const handleStatusChange = (key) => (statuses) => {
        setStatusDraft((current) => ({...current, [key]: statuses}));
    };

    const hasDraftChanges = hasOrderStatusDraftChanges(statusSettings, statusDraft);

    return (
        <NarrowSecureLayout title={t('project.order-statuses')}>
            <LoadingComponent loading={statusSettings === null || updating}>
                <div className="integration-detail-form flex flex-col gap-5">
                    <StatusCategorySection
                        title={t('project.statuses.completed')}
                        description={t('project.statuses.completed-description')}
                        options={Object.values(statusLists?.completedPossible ?? {})}
                        value={statusLists.completed}
                        onChange={handleStatusChange('completed')}
                        noOptionsMessage={() => t('project.integration-no-options')}
                    />
                    <StatusCategorySection
                        title={t('project.statuses.received')}
                        description={t('project.statuses.received-description')}
                        options={Object.values(statusLists?.receivedPossible ?? {})}
                        value={statusLists.received}
                        onChange={handleStatusChange('received')}
                        noOptionsMessage={() => t('project.integration-no-options')}
                    />
                    <StatusCategorySection
                        title={t('project.statuses.cancelled')}
                        description={t('project.statuses.cancelled-description')}
                        options={Object.values(statusLists?.cancelledPossible ?? {})}
                        value={statusLists.cancelled}
                        onChange={handleStatusChange('cancelled')}
                        noOptionsMessage={() => t('project.integration-no-options')}
                    />
                    <div className="flex justify-end border-t border-slate-200 pt-5 dark:border-slate-700">
                        <Button
                            className="button-primary"
                            disabled={!hasDraftChanges || updating}
                            onClick={saveStatusDraft}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </div>

            </LoadingComponent>
            <div className="mt-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 shadow-sm dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 shrink-0 text-base text-amber-500 dark:text-amber-300"/>
                <div className="space-y-1.5 leading-6">
                    <p>{t('project.statuses.not-seeing-means-not-yet-downloaded')}</p>
                    <p>{t('project.statuses.statuses-are-not-applied-immediately')}</p>
                </div>
            </div>
        </NarrowSecureLayout>
    );
}
